import { useState, useEffect, useCallback } from 'react';
import { PageHeader, Card, LoadingSpinner } from '../components/Layout';
import { api } from '../services/api';

interface BrevoList {
  id: number;
  name: string;
  uniqueSubscribers?: number;
}

interface BrevoCampaign {
  id: number;
  name: string;
  subject?: string;
  status: string;
  scheduledAt?: string;
  statistics?: { globalStats?: Record<string, number> };
}

const emptyEmail = {
  name: '',
  subject: '',
  htmlContent: '',
  listId: '' as string,
  mode: 'draft' as 'draft' | 'now' | 'schedule',
  scheduledAt: '',
};

export default function MarketingPage() {
  const [lists, setLists] = useState<BrevoList[]>([]);
  const [campaigns, setCampaigns] = useState<BrevoCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyEmail);
  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [listRes, campRes] = await Promise.all([
        api.get<{ lists?: BrevoList[] }>('/brevo/lists').catch(() => null),
        api.get<{ campaigns?: BrevoCampaign[] }>('/brevo/campaigns'),
      ]);
      setLists(listRes?.lists || []);
      setCampaigns(campRes?.campaigns || []);
    } catch (e: any) {
      // Surfaces Brevo's own message — e.g. the "authorise IP" hint — verbatim.
      setError(e?.message || 'Failed to load Brevo data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const stat = (c: BrevoCampaign, key: string): number | string => {
    const v = c.statistics?.globalStats?.[key];
    return v === undefined ? '—' : v;
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setNotice(null);
    if (!form.name || !form.subject || !form.htmlContent || !form.listId) {
      setNotice('Name, subject, body, and a target list are all required.');
      return;
    }
    setSubmitting(true);
    try {
      const body: any = {
        name: form.name,
        subject: form.subject,
        htmlContent: form.htmlContent,
        listIds: [Number(form.listId)],
        sendNow: form.mode === 'now',
      };
      if (form.mode === 'schedule' && form.scheduledAt) {
        body.scheduledAt = new Date(form.scheduledAt).toISOString();
      }
      const res = await api.post<{ campaignId: number; sent: boolean }>('/brevo/campaigns', body);
      const { campaignId, sent } = res.data;
      setNotice(
        campaignId
          ? sent
            ? `Campaign #${campaignId} created and sent.`
            : `Campaign #${campaignId} saved${form.mode === 'schedule' ? ' and scheduled' : ' as a draft'}.`
          : 'Campaign created.',
      );
      setForm(emptyEmail);
      setShowForm(false);
      load();
    } catch (e: any) {
      setNotice(e?.message || 'Failed to create campaign');
    } finally {
      setSubmitting(false);
    }
  };

  const sendDraft = async (id: number) => {
    if (!confirm(`Send campaign #${id} now?`)) return;
    setNotice(null);
    try {
      await api.post(`/brevo/campaigns/${id}/send`, {});
      setNotice(`Campaign #${id} sent.`);
      load();
    } catch (e: any) {
      setNotice(e?.message || 'Failed to send campaign');
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <PageHeader
        title="Marketing — Cold Prospecting"
        subtitle="Cold email campaigns run through Brevo to protect our sending domain. A click promotes a prospect to a warm lead in the Sales Portal."
        actions={
          <button
            onClick={() => setShowForm((s) => !s)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
          >
            {showForm ? 'Close' : 'New Campaign'}
          </button>
        }
      />

      {notice && (
        <div className="rounded-lg bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 text-sm">
          {notice}
        </div>
      )}

      {error && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 text-sm">
          <strong>Brevo unavailable:</strong> {error}
        </div>
      )}

      {showForm && (
        <Card title="New cold campaign">
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Campaign name (internal)
                </label>
                <input
                  className="input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="July dealer recruitment"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Target list</label>
                <select
                  className="input"
                  value={form.listId}
                  onChange={(e) => setForm({ ...form, listId: e.target.value })}
                >
                  <option value="">Select a list…</option>
                  {lists.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.name}
                      {l.uniqueSubscribers !== undefined ? ` (${l.uniqueSubscribers})` : ''}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Subject line</label>
              <input
                className="input"
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                placeholder="Become a Keder Screens dealer"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email body (HTML)</label>
              <textarea
                className="input font-mono h-48"
                value={form.htmlContent}
                onChange={(e) => setForm({ ...form, htmlContent: e.target.value })}
                placeholder="<h1>Partner with Keder Screens</h1>…<a href='https://kederscreens.com/apply'>Apply</a>"
              />
              <p className="text-xs text-gray-500 mt-1">
                Include a tracked link (e.g. your apply page) — a click on it is what promotes the
                prospect to a warm lead.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-4">
              {(['draft', 'now', 'schedule'] as const).map((m) => (
                <label key={m} className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    checked={form.mode === m}
                    onChange={() => setForm({ ...form, mode: m })}
                  />
                  {m === 'draft' ? 'Save as draft' : m === 'now' ? 'Send now' : 'Schedule'}
                </label>
              ))}
              {form.mode === 'schedule' && (
                <input
                  type="datetime-local"
                  className="border rounded-lg px-3 py-2 text-sm"
                  value={form.scheduledAt}
                  onChange={(e) => setForm({ ...form, scheduledAt: e.target.value })}
                />
              )}
            </div>

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setForm(emptyEmail);
                  setShowForm(false);
                }}
                className="px-4 py-2 border rounded-lg text-sm"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
              >
                {submitting
                  ? 'Saving…'
                  : form.mode === 'now'
                    ? 'Create & Send'
                    : form.mode === 'schedule'
                      ? 'Create & Schedule'
                      : 'Save Draft'}
              </button>
            </div>
          </form>
        </Card>
      )}

      <Card title="Campaigns">
        {loading ? (
          <div className="py-10 flex justify-center">
            <LoadingSpinner />
          </div>
        ) : campaigns.length === 0 ? (
          <p className="text-sm text-gray-500 py-6 text-center">
            No campaigns yet. Create one above to start cold outreach.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="py-2 pr-4">Name</th>
                  <th className="py-2 pr-4">Subject</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2 pr-4 text-right">Sent</th>
                  <th className="py-2 pr-4 text-right">Opens</th>
                  <th className="py-2 pr-4 text-right">Clicks</th>
                  <th className="py-2 pr-4"></th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((c) => (
                  <tr key={c.id} className="border-b last:border-0">
                    <td className="py-2 pr-4 font-medium text-gray-900">{c.name}</td>
                    <td className="py-2 pr-4 text-gray-600">{c.subject}</td>
                    <td className="py-2 pr-4">
                      <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-700">
                        {c.status}
                      </span>
                    </td>
                    <td className="py-2 pr-4 text-right">{stat(c, 'sent')}</td>
                    <td className="py-2 pr-4 text-right">{stat(c, 'uniqueViews')}</td>
                    <td className="py-2 pr-4 text-right font-semibold text-blue-600">
                      {stat(c, 'uniqueClicks')}
                    </td>
                    <td className="py-2 pr-4 text-right">
                      {c.status === 'draft' && (
                        <button
                          onClick={() => sendDraft(c.id)}
                          className="text-xs px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700"
                        >
                          Send
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
